import Vue from 'vue'
import domUtils from 'util/dom-utils'
import apiService, { RestError } from 'services/api-service'
import { debounce } from 'util/ts-utils'
import Ask from 'components/ask/ask'
import { getOfflineOrUnexpectedMsg } from 'services/msg'
import { getUserToken } from 'services/token-service'
import globalConfig from 'init/global.config'
import globalStore, { Store } from 'init/global-store'
import { hostsConfig } from 'init/config'
import iaConfig from 'widgets/instant-answers/instant-answers.config'
import MultiItems from 'components/question/multi-items'
import qaConfig, { RelatedQuestionsDisplayTypes } from 'widgets/qa/qa.config'
import reviewsListConfig, { RelatedReviewsDisplayTypes }
  from 'widgets/reviews-list/reviews-list-widget.config'
import { ssoCreateQuestion } from 'services/sso-service'
import standalonePopoverLoader from 'widgets/standalone-popover/standalone-popover-loader'
import submissionLoader from 'widgets/submission/submission-loader'
import submissionStore from 'store/submission-store'
import UserToolbar from 'components/user-toolbar/user-toolbar'
import validator from 'util/validation-utils'

import withRender from './instant-answers.html'
import './instant-answers.scss'

const MAX_CHARACTERS = 2000
const CHARACTER_THRESHOLD = 200

export default withRender(Vue.extend({
  name: 'InstantAnswers',
  data() {
    return {
      globalStore,
      inAskMode: false, // is true when user has clicked button to submit new question
      isOffline: false,
      isSearching: false,
      isSubmitting: false,
      errorText: null,
      multiItems: [],
      previousQuestionText: '',
      questionText: '',
      searchResults: []
    }
  },
  components: {
    'tt-ask': Ask,
    'tt-multi-items': MultiItems,
    // tslint:disable-next-line
    'tt-ask-results': () => import(/* webpackChunkName: 'ask-results' */ 'components/ask-results/ask-results'),
    'tt-user-toolbar': UserToolbar
  },
  computed: {
    charactersLeft() {
      return MAX_CHARACTERS - this.trimmedQuestionText.length
    },
    countText(): string {
      return (this.charactersLeft <= CHARACTER_THRESHOLD) ?
        this.$msg('cc.charactersLeft', [this.charactersLeft]) : ''
    },
    currentSku(): string {
      return (this.globalStore as Store).sku
    },
    iaConfig() {
      return iaConfig
    },
    multiItemsPromptText() {
      return this.$msg(`multiItem.question.prompt${this.multiItems.length > 0 ? '.withAdded' : ''}`)
    },
    resultsPromptText() {
      return this.searchResults.length > 0 ? this.$msg('ia.results.notWhatLookingFor') :
        this.$msg('ia.results.reviseSearch')
    },
    showMultiItems() {
      return iaConfig.multiItemQuestionEnabled && this.inAskMode
    },
    showResults() {
      return this.trimmedQuestionText && !this.inAskMode
    },
    trimmedQuestionText() {
      return this.questionText.trim()
    }
  },
  methods: {
    addMultiItems(items) {
      items.forEach((item) => {
        if (this.multiItems.filter(mi => mi.id === item.id).length === 0) {
          this.multiItems.push(item)
        }
      })
    },
    askUpdate() {
      const trimmedText = this.trimmedQuestionText
      // don't want to trigger unnecessary searces
      if (trimmedText === this.previousQuestionText) {
        return
      }
      this.previousQuestionText = trimmedText
      if (this.charactersLeft < 0) {
        this.errorText = this.$msg('cc.charactersOver', [Math.abs(this.charactersLeft)])
        return
      }
      this.clearErrors()
      if (trimmedText.length === 0) {
        this.inAskMode = false
        this.searchResults = []
      } else if (!this.inAskMode) {
        this.instantAnswerSearch()
      }
    },
    cancelAskMode() {
      this.inAskMode = false
      this.questionText = ''
      this.clearErrors()
    },
    clearErrors() {
      this.errorText = ''
    },
    enterAskMode() {
      this.inAskMode = true
      domUtils.safeFocusRef(this.$refs, 'submitBtn')
    },
    instantAnswerSearch: debounce(function () { // eslint-disable-line func-names
      this.isSearching = true
      this.isOffline = false
      apiService.instantAnswerSearch({
        catalogItemId: globalStore.catItemId,
        categoryIds: globalStore.parentCategoryIds,
        brand: globalStore.brand,
        includeRelatedQuestions:
          qaConfig.relatedQuestionsDisplay === RelatedQuestionsDisplayTypes.INCLUDED,
        includeRelatedReviews:
          reviewsListConfig.relatedReviewsDisplay === RelatedReviewsDisplayTypes.INCLUDED,
        searchTerm: this.trimmedQuestionText
      }).then((data) => {
        this.searchResults = data.items
        this.isSearching = false
      }).catch((e: RestError) => {
        if (e.errorCode === 'turnto-offline') {
          this.isOffline = true
        }
        this.isSearching = false
      })
    }, 300), // TODO: speed up with more words as in current tra code?
    onShowError(e) {
      if (e.fieldErrors.length > 0 && e.fieldErrors[0].code === 'ext-site-link') {
        this.errorText = this.$msg('ia.err.containsLink')
        this.isSubmitting = false
      } else {
        // used for error like question submission where modal not open
        standalonePopoverLoader.load({ message: getOfflineOrUnexpectedMsg(e),
          type: 'error', autoClose: true })
        this.resetAfterError()
      }
    },
    removeMultiItem(item) {
      for (let i = 0; i < this.multiItems.length; i++) {
        if (this.multiItems[i].id === item.id) {
          this.multiItems.splice(i, 1)
          break
        }
      }
    },
    resetAfterError() {
      // so we don't wipe out question giving them option of trying again
      // which may only be practical for offline error case
      this.inAskMode = false
      this.isSearching = false
      this.isSubmitting = false
      this.isOffline = false
    },
    resetAfterSubmit() {
      this.cancelAskMode()
      this.isSearching = false
      this.isSubmitting = false
      this.isOffline = false
      this.searchResults = []
      this.multiItems = []
    },
    validateQuestion(text) {
      this.clearErrors()
      if (this.charactersLeft < 0) {
        this.errorText = this.$msg('cc.charactersOver', [Math.abs(this.charactersLeft)])
        return false
      }
      if (text.search(/[?]/im) === -1) {
        this.errorText = this.$msg('ia.error.isThisAQuestion')
        return false
      }
      if (validator.containsEmail(text)) {
        this.errorText = this.$msg('ia.err.dontIncludeEmail')
        return false
      }
      if (validator.containsPhoneNumber(text)) {
        this.errorText = this.$msg('ia.err.dontIncludePhone')
        return false
      }
      return true
    },
    submitQuestion() {
      this.isSubmitting = true
      if (!this.validateQuestion(this.trimmedQuestionText)) {
        const field = this.$el.querySelector('.tt-o-search-field__input')
        domUtils.safeFocus(field).then(() => {
          // Safari was having an issue focusing at end of content in textarea.
          // Most likely because it's a contenteditable div instead of an actual textarea.
          domUtils.setCursorAtEndOfText(field)
        })
        this.isSubmitting = false
        return
      }
      const userToken = getUserToken()
      if (userToken) {
        // This is a logged in, and therefore confirmed user.
        apiService.createQuestionConfirmedUser({
          'context-token': userToken,
          sku: this.currentSku
        }, {
          questionType: 0,
          text: this.trimmedQuestionText,
          multiItemIds: this.multiItems.map(item => item.id)
        }).then((data) => {
          // go to do more where question submission message will display
          submissionStore.startFlow(
            this.currentSku,
            hostsConfig.siteKey,
            { page: 'do-more', props: { msgType: 'question' } }
          )
          submissionLoader.load()

          this.onQuestionSubmit(data.questionId)
          this.resetAfterSubmit()
        }).catch((e: RestError) => {
          this.onShowError(e)
        })
      } else {
        const qualifier = globalConfig.isAllowAnon() ? 'anonymous' : 'unconfirmed'
        apiService.createQuestionAnonOrUnconfirmedUser({
          sku: this.currentSku,
          qualifier
        }, {
          questionType: 0,
          text: this.trimmedQuestionText,
          multiItemIds: this.multiItems.map(item => item.id)
        }).then((resp) => {
          if (resp.questionId) {
            this.onQuestionSubmit(resp.questionId)
          }
          this.resetAfterSubmit()
          if (globalConfig.ssoEnabled) {
            ssoCreateQuestion(this.currentSku, resp.savedQuestionToken || resp.stagedQuestionToken)
          } else {
            // tslint:disable-next-line
            import(/* webpackChunkName: 'registration-modal' */ 'components/registration/registration-modal-loader').then((registrationModalLoader) => {
              registrationModalLoader.default.load({
                // for anonymous, will be null for other qualifier types
                savedContentToken: resp.savedQuestionToken,
                // for emailRequired and emailConfirmRequired, will be null if for anonymous
                stagedContentToken: resp.stagedQuestionToken,
                registerFor: 'question'
              })
            })
          }
        }).catch((e: RestError) => {
          this.onShowError(e)
        })
      }
    },
    onInstantAnswerClick() {
      // if client provided an onInstantAnswerClick function
      const eventHandlers = window.turnToConfig.eventHandlers || {}
      if (eventHandlers.onInstantAnswerClick) {
        this.$nextTick(() => {
          eventHandlers.onInstantAnswerClick()
        })
      }
    },
    onQuestionSubmit(questionId: number) {
      // if client provided an onQuestionSubmit function
      const eventHandlers = window.turnToConfig.eventHandlers || {}
      if (eventHandlers.onQuestionSubmit) {
        eventHandlers.onQuestionSubmit({ id: questionId })
      }
    }
  },
  watch: {
    questionText() {
      this.askUpdate()
    }
  }
}))



// WEBPACK FOOTER //
// ./node_modules/tslint-loader??ref--1!(webpack)-strip-block!./src/widgets/instant-answers/instant-answers.ts